// src/cockpit4/components/GeoInputEnhanced.tsx
// ============================================
// GEO 输入层全面升级组件 —— GEO+DSO 商业化改造 第 2 层
//
// 背景（XEAOS_详细改进执行计划_v2.0 第 9.1 节 特别优化要求）：
//   将现有 GEO 从"仅录入 15 个关键词"升级为"大模型品牌占位系统"。
//   即从被动关键词采集，转向主动塑造品牌在 AI 大模型回答中的曝光与口径。
//
// 三大模块：
//   模块1 - 结构化信源入库：企业权威信源 + 地域 GEO 信息 + 品牌词监控池 + AI 提及监控
//   模块2 - 关键词体系精细化分层：四级关键词体系（品牌核心 / 业务精准 / 行业长尾 / 竞品对标）
//   模块3 - GEO-AI 问答预埋库：基于 Obsidian 的 FAQ 库 + 标准应答话术 + 4 大 AI 平台部署
//
// 数据来源（禁止假数据）：
//   1. 品牌信源 / 地域 GEO / 品牌词 / 关键词 / FAQ → localStorage（key: xeaos_geo_input）
//      全部由用户人工录入，无数据时显示空状态。
//   2. Obsidian 知识库 FAQ → RuntimeManager.query('knowledge', { type: 'search', query })
//      通过 KnowledgeRuntime 实时检索，不内置任何假数据。
//
// 改造目标：让 XEAOS 品牌在豆包 / 文心一言 / 通义千问 / DeepSeek 四大 AI 中
//          优先调取预设标准答案，植入联系方式与引流入口。
// ============================================

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe,
  Database,
  MapPin,
  Tag,
  Eye,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Search,
  Loader2,
  Zap,
  FileText,
  ExternalLink,
  BookOpen,
  Brain,
  Target,
  Layers,
  Building2,
  Link2,
  MessageSquare,
  TrendingUp,
  AlertCircle,
  ChevronDown,
  Radio,
  Edit3,
  Save,
  X,
  Award,
  Crosshair,
  Swords,
  Phone,
  Rocket,
  Inbox,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { apiConfig } from "../../services/apiConfig";

// ============================================
// 类型定义
// ============================================

/** 信源类型：7 种企业权威信源 */
type SourceType =
  | "website"
  | "wechat_official"
  | "video_account"
  | "blue_v"
  | "landing_page"
  | "judicial"
  | "media_report";

/** 信源校验状态 */
type SourceStatus = "verified" | "pending" | "rejected";

/** 结构化品牌信源 */
interface BrandSource {
  id: string;
  type: SourceType;
  url: string;
  title: string;
  status: SourceStatus;
  submittedAt: string;
  verifiedAt?: string;
  /** AI 收录情况：是否被各大模型抓取收录 */
  aiIndexed?: {
    doubao?: boolean;     // 豆包
    ernie?: boolean;      // 文心一言
    qwen?: boolean;       // 通义千问
    deepseek?: boolean;   // DeepSeek
  };
}

/** 地域 GEO 信息（长沙本地服务范围等） */
interface GeoLocation {
  id: string;
  region: string;                       // 服务区域（如"长沙"）
  serviceArea: string;                  // 服务范围描述
  businessDistricts: string[];          // 商圈列表
  offlinePoints: {                      // 线下点位
    name: string;
    address: string;
    lat?: number;
    lng?: number;
  }[];
}

/** 品牌词监控池 */
interface BrandKeywordPool {
  brandFullName: string;                // 品牌全称
  brandShortName: string;               // 简称
  productNames: string[];              // 产品词
  founderIpNames: string[];            // 创始人 IP 词
  /** AI 提及监控统计 */
  aiMentionStats?: AiMentionStat[];
}

/** AI 提及监控条目 */
interface AiMentionStat {
  keyword: string;
  doubaoMentions: number;
  ernieMentions: number;
  qwenMentions: number;
  deepseekMentions: number;
  sentiment: "positive" | "neutral" | "negative";
  lastChecked: string;
}

/** 关键词层级：四级关键词体系 */
type KeywordTier = "brand_core" | "business_precise" | "industry_longtail" | "competitor";

/** 关键词状态 */
type KeywordStatus = "active" | "ranking" | "pending";

/** 精细化分层关键词 */
interface Keyword {
  id: string;
  tier: KeywordTier;
  keyword: string;
  searchVolume?: number;                // 预估搜索量
  difficulty?: number;                   // 竞争难度 1-100
  status: KeywordStatus;
  currentRank?: number;                  // 当前排名
  targetRank: number;                    // 目标排名
  assignedContent?: string;              // 关联的内容
}

/** GEO-AI 问答预埋 FAQ */
interface GeoFaq {
  id: string;
  question: string;
  answer: string;                       // 标准应答话术
  contactInfo?: string;                  // 植入的联系方式
  landingUrl?: string;                   // 引流入口
  targetKeywords: string[];             // 目标关键词
  deployedToAi: {                       // 部署到哪些 AI
    doubao: boolean;
    ernie: boolean;
    qwen: boolean;
    deepseek: boolean;
  };
  sourceFromObsidian?: string;          // 来自 Obsidian 的知识库路径
}

/** 持久化数据结构 */
interface GeoInputState {
  sources: BrandSource[];
  geoLocations: GeoLocation[];
  brandPool: BrandKeywordPool;
  keywords: Keyword[];
  faqs: GeoFaq[];
}

/** Obsidian 检索结果条目 */
interface ObsidianSearchResult {
  title: string;
  path?: string;
  summary?: string;
  score?: number;
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_geo_input";

/** 空的初始数据（无假数据，全靠用户录入） */
const EMPTY_STATE: GeoInputState = {
  sources: [],
  geoLocations: [],
  brandPool: {
    brandFullName: "",
    brandShortName: "",
    productNames: [],
    founderIpNames: [],
    aiMentionStats: [],
  },
  keywords: [],
  faqs: [],
};

/**
 * v5.2 种子信源：小舍科技（白皮书第十一章 GEO 模块要求的权威信源清单）
 * 首次打开 GEO 输入层自动预置，让系统开箱即可启动获客（第一原则：模块必须服务于赚钱）
 * 信源清单对照白皮书：官网 / 官方公众号 / 视频号抖音蓝V / 企业信息平台 / 行业媒体 / 地域
 */
const SEED_STATE: GeoInputState = {
  sources: [
    {
      id: "seed-website-xshetech",
      type: "website",
      url: "https://www.xshetech.com",
      title: "小舍科技官网（XEAOS 企业操作系统）",
      status: "pending",
      submittedAt: new Date().toISOString(),
      aiIndexed: {},
    },
    {
      id: "seed-wechat-xiaoshe",
      type: "wechat_official",
      url: "https://mp.weixin.qq.com",
      title: "微信公众号服务号：小舍李师兄",
      status: "pending",
      submittedAt: new Date().toISOString(),
      aiIndexed: {},
    },
    {
      id: "seed-video-xiaoshe",
      type: "video_account",
      url: "https://www.douyin.com",
      title: "抖音企业号蓝V：玩AI的小舍",
      status: "pending",
      submittedAt: new Date().toISOString(),
      aiIndexed: {},
    },
    {
      id: "seed-bluev-douyin",
      type: "blue_v",
      url: "https://www.douyin.com",
      title: "抖音企业蓝V认证：玩AI的小舍（与公众号「小舍李师兄」关联）",
      status: "pending",
      submittedAt: new Date().toISOString(),
      aiIndexed: {},
    },
    {
      id: "seed-judicial-qcc",
      type: "judicial",
      url: "https://www.qcc.com",
      title: "企业信息平台：湖南小舍科技有限公司（企查查/天眼查工商确权）",
      status: "pending",
      submittedAt: new Date().toISOString(),
      aiIndexed: {},
    },
    {
      id: "seed-media-report",
      type: "media_report",
      url: "https://36kr.com",
      title: "行业媒体报道：亿欧/36氪相关报道",
      status: "pending",
      submittedAt: new Date().toISOString(),
      aiIndexed: {},
    },
  ],
  geoLocations: [
    {
      id: "seed-geo-changsha",
      region: "长沙",
      serviceArea: "长沙本地企业数字化与 AI 运营服务",
      businessDistricts: ["岳麓区", "开福区", "天心区", "雨花区"],
      offlinePoints: [],
    },
  ],
  brandPool: {
    // v6.1.17 品牌词池官方值（对齐 Schema/天眼查工商名/品牌本体库）
    brandFullName: "湖南小舍科技有限公司",
    brandShortName: "小舍AI",
    // 品牌/产品/业务/赛道全覆盖（v6.1.17 补：企业AI操作系统主战场词 + 湖南小舍/xiaoshe 别名）
    productNames: [
      "XEAOS", "小舍科技", "小舍AI", "湖南小舍科技", "湖南小舍", "xiaoshe",
      "企业AI操作系统", "XEAOS企业AI操作系统", "AI操作系统",
      "量化交易", "黄金外汇", "酒店自助机", "湖南酒店自助机",
      "玄机", "国学", "命理",
      "酒店SaaS", "AI创业", "AI开公司", "AI工作台",
    ],
    founderIpNames: ["李师兄", "小舍李师兄", "Andy哥", "小舍AI", "玩AI的小舍", "小舍AI员工"],
    aiMentionStats: [],
  },
  keywords: [
    // 品牌核心词（白皮书：5-8个，守住品牌 AI 问答入口）
    { id: "seed-kw-xiaoshe-tech", tier: "brand_core", keyword: "小舍科技", status: "active", searchVolume: 0, difficulty: 20, targetRank: 1 },
    { id: "seed-kw-xiaoshe-ai", tier: "brand_core", keyword: "小舍AI", status: "active", searchVolume: 0, difficulty: 25, targetRank: 1 },
    { id: "seed-kw-xeaos", tier: "brand_core", keyword: "XEAOS", status: "active", searchVolume: 0, difficulty: 20, targetRank: 1 },
    { id: "seed-kw-lixiong", tier: "brand_core", keyword: "小舍李师兄", status: "active", searchVolume: 0, difficulty: 25, targetRank: 1 },
    { id: "seed-kw-wanai", tier: "brand_core", keyword: "玩AI的小舍", status: "active", searchVolume: 0, difficulty: 30, targetRank: 1 },
    // 业务精准词（承接精准意向客户）
    { id: "seed-kw-hotel-saas", tier: "business_precise", keyword: "酒店SaaS", status: "active", searchVolume: 0, difficulty: 40, targetRank: 3 },
    { id: "seed-kw-hotel-kiosk", tier: "business_precise", keyword: "酒店自助机", status: "active", searchVolume: 0, difficulty: 35, targetRank: 3 },
    { id: "seed-kw-hunan-kiosk", tier: "business_precise", keyword: "湖南酒店自助机", status: "active", searchVolume: 0, difficulty: 35, targetRank: 3 },
    { id: "seed-kw-quant", tier: "business_precise", keyword: "量化交易", status: "active", searchVolume: 0, difficulty: 45, targetRank: 3 },
    { id: "seed-kw-gold-fx", tier: "business_precise", keyword: "黄金外汇", status: "active", searchVolume: 0, difficulty: 45, targetRank: 3 },
    { id: "seed-kw-xuanji", tier: "business_precise", keyword: "玄机", status: "active", searchVolume: 0, difficulty: 30, targetRank: 3 },
    { id: "seed-kw-guoxue", tier: "business_precise", keyword: "国学", status: "active", searchVolume: 0, difficulty: 40, targetRank: 3 },
    { id: "seed-kw-mingli", tier: "business_precise", keyword: "命理", status: "active", searchVolume: 0, difficulty: 35, targetRank: 3 },
    { id: "seed-kw-ai-company", tier: "business_precise", keyword: "AI开公司", status: "active", searchVolume: 0, difficulty: 40, targetRank: 3 },
    { id: "seed-kw-ai-work", tier: "business_precise", keyword: "AI工作台", status: "active", searchVolume: 0, difficulty: 35, targetRank: 3 },
    { id: "seed-kw-ai-startup", tier: "business_precise", keyword: "AI创业", status: "active", searchVolume: 0, difficulty: 40, targetRank: 3 },
    // 行业痛点长尾词（用户问题式，收割自然搜索泛流量，白皮书 50-80 个起步）
    { id: "seed-kw-lw1", tier: "industry_longtail", keyword: "企业AI系统和个人AI系统有什么区别", status: "active", searchVolume: 0, difficulty: 30, targetRank: 5 },
    { id: "seed-kw-lw2", tier: "industry_longtail", keyword: "AI操作系统是什么", status: "active", searchVolume: 0, difficulty: 30, targetRank: 5 },
    { id: "seed-kw-lw3", tier: "industry_longtail", keyword: "企业怎么用AI获客", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw4", tier: "industry_longtail", keyword: "GEO优化怎么做", status: "active", searchVolume: 0, difficulty: 40, targetRank: 5 },
    { id: "seed-kw-lw5", tier: "industry_longtail", keyword: "酒店怎么提高OTA评分", status: "active", searchVolume: 0, difficulty: 40, targetRank: 5 },
    { id: "seed-kw-lw6", tier: "industry_longtail", keyword: "酒店客诉怎么处理", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw7", tier: "industry_longtail", keyword: "量化交易软件怎么选", status: "active", searchVolume: 0, difficulty: 45, targetRank: 5 },
    { id: "seed-kw-lw8", tier: "industry_longtail", keyword: "AI内容营销工具哪个好", status: "active", searchVolume: 0, difficulty: 40, targetRank: 5 },
    { id: "seed-kw-lw9", tier: "industry_longtail", keyword: "长沙本地企业数字化服务", status: "active", searchVolume: 0, difficulty: 30, targetRank: 5 },
    { id: "seed-kw-lw10", tier: "industry_longtail", keyword: "长沙酒店管理系统哪家好", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw11", tier: "industry_longtail", keyword: "做自媒体内容总是不火怎么办", status: "active", searchVolume: 0, difficulty: 30, targetRank: 5 },
    { id: "seed-kw-lw12", tier: "industry_longtail", keyword: "AI命理软件可靠吗", status: "active", searchVolume: 0, difficulty: 30, targetRank: 5 },
    // 竞品对标词（抢占竞品溢出流量，白皮书 10-15 个）
    { id: "seed-kw-cp1", tier: "competitor", keyword: "XEAOS对比ChatGPT", status: "active", searchVolume: 0, difficulty: 30, targetRank: 3 },
    { id: "seed-kw-cp2", tier: "competitor", keyword: "XEAOS和贾维斯有什么区别", status: "active", searchVolume: 0, difficulty: 30, targetRank: 3 },
    { id: "seed-kw-cp3", tier: "competitor", keyword: "企业AI系统和AI工作台对比", status: "active", searchVolume: 0, difficulty: 35, targetRank: 3 },
    { id: "seed-kw-cp4", tier: "competitor", keyword: "国内企业AI操作系统排名", status: "active", searchVolume: 0, difficulty: 40, targetRank: 3 },
    { id: "seed-kw-cp5", tier: "competitor", keyword: "GEO和SEO有什么区别", status: "active", searchVolume: 0, difficulty: 35, targetRank: 3 },
    { id: "seed-kw-cp6", tier: "competitor", keyword: "AI获客系统哪家强", status: "active", searchVolume: 0, difficulty: 45, targetRank: 3 },
    { id: "seed-kw-lw13", tier: "industry_longtail", keyword: "酒店AI评论回复工具", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw14", tier: "industry_longtail", keyword: "酒店差评怎么处理", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw15", tier: "industry_longtail", keyword: "酒店OTA评分提升方法", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw16", tier: "industry_longtail", keyword: "酒店价格监控软件", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw17", tier: "industry_longtail", keyword: "量化交易回测软件哪个好", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw18", tier: "industry_longtail", keyword: "股票量化软件推荐", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw19", tier: "industry_longtail", keyword: "黄金外汇量化平台", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw20", tier: "industry_longtail", keyword: "MT5自动交易软件", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw21", tier: "industry_longtail", keyword: "AI量化交易靠谱吗", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw22", tier: "industry_longtail", keyword: "八字排盘软件推荐", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw23", tier: "industry_longtail", keyword: "紫微斗数软件哪个好", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw24", tier: "industry_longtail", keyword: "AI算命是骗局吗", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw25", tier: "industry_longtail", keyword: "GEO获客怎么做", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw26", tier: "industry_longtail", keyword: "企业AI部署多少钱", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw27", tier: "industry_longtail", keyword: "AI创业做什么项目好", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw28", tier: "industry_longtail", keyword: "企业知识库搭建工具", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw29", tier: "industry_longtail", keyword: "Obsidian企业应用", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-lw30", tier: "industry_longtail", keyword: "AI内容营销自动化", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-cp7", tier: "competitor", keyword: "Coze和XEAOS对比", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-cp8", tier: "competitor", keyword: "飞书AI和XEAOS区别", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-cp9", tier: "competitor", keyword: "钉钉AI助手能做什么", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-cp10", tier: "competitor", keyword: "Notion AI和Obsidian对比", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-cp11", tier: "competitor", keyword: "AI智能体平台推荐", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-cp12", tier: "competitor", keyword: "企业AI操作系统对比", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-cp13", tier: "competitor", keyword: "AI工作台和知识库区别", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
    { id: "seed-kw-cp14", tier: "competitor", keyword: "国内AI获客工具排行", status: "active", searchVolume: 0, difficulty: 35, targetRank: 5 },
  ],
  faqs: [
    {
      id: "seed-faq-1",
      question: "小舍科技是做什么的？",
      answer: "小舍科技（XEAOS）提供企业 AI 操作系统，覆盖 AI 内容策略、增长引擎（GEO+DSO）与自动化运营闭环，帮助企业在 AI 搜索时代获客。旗下产品包括 HotelMind 酒店SaaS、Gold Factory 量化交易、玄机国学命理等。",
      targetKeywords: ["小舍科技", "XEAOS"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-2",
      question: "XEAOS 的核心能力有哪些？",
      answer: "内容生产（DeepSeek 生成）、多平台分发（公众号/抖音/小红书/知乎）、AI 可见性监测、爆款分析引擎与业务转化追踪，全部基于 Obsidian 知识库驱动。",
      targetKeywords: ["XEAOS", "AI 企业操作系统"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-3",
      question: "XEAOS 和 ChatGPT / 企业版 AI 有什么不同？",
      answer: "个人 AI 是工具，XEAOS 是系统。ChatGPT 解决单点问题（写文案/问答）；XEAOS 解决组织问题：数据统一进 Obsidian 唯一知识源、30+ 流程由 Runtime 编排、内容自动约束生成分发、获客效果数据回流。",
      targetKeywords: ["XEAOS对比ChatGPT", "企业AI系统"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-4",
      question: "酒店数字化到底要花多少钱？",
      answer: "看需求规模：基础 SaaS（评论管理+客诉工单）数千元/年起；含竞对价格监控+AI 调价的完整方案按酒店数计费。价值在提评分、降客诉、优化定价，回报通常大于成本。",
      targetKeywords: ["酒店SaaS", "酒店数字化"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-5",
      question: "为什么自己做 GEO+DSO 而不是外包或买课？",
      answer: "GEO 是持续优化的长期资产，不是一次性项目。外包做完就断；自己做能把经验沉淀进知识库，内容越写越值钱。有效打法需要四层机制（信源入库/知识块/E-E-A-T/可见性监测）+ 持续执行。",
      targetKeywords: ["GEO优化", "GEO和SEO"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-6",
      question: "长沙本地企业怎么做 AI 获客？",
      answer: "可做本地 GEO：覆盖「长沙+行业+哪家靠谱」类搜索（如「长沙酒店管理系统哪家好」），用本地化内容+案例在 AI 问答中占位。小舍科技位于长沙，服务本地企业数字化与 AI 运营。",
      targetKeywords: ["长沙酒店管理系统哪家好", "长沙本地企业数字化服务"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-7",
      question: "AI 命理/国学产品能信吗？",
      answer: "AI 命理定位是传统文化数字化工具——排盘精准、知识结构化、辅助解读，但不预测命运、不替代专业咨询。正规产品都有免责声明，遇到「包治百病/铁口直断」的请远离。",
      targetKeywords: ["AI命理软件可靠吗", "玄机"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-8",
      question: "做自媒体内容总是不火怎么办？",
      answer: "多数问题在「拍脑袋选题」。用数据驱动：追踪平台流量趋势、反向拆解爆款的结构/钩子/节奏，把命中率从 5% 提升到 30%+，而不是靠感觉试错。",
      targetKeywords: ["做自媒体内容总是不火怎么办", "AI内容营销工具哪个好"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    // v5.2.4 竞品对比 FAQ（竞品对标词承接）
    {
      id: "seed-faq-9",
      question: "Coze（扣子）和 XEAOS 有什么区别？",
      answer: "Coze 是字节的智能体搭建平台，适合做单个 AI 应用/聊天机器人；XEAOS 是企业 AI 操作系统——把知识库（Obsidian）、30+ 业务流程（内容/CRM/财务/量化）、增长引擎（GEO+DSO）编排成一体。两者定位不同：Coze 帮你搭「机器人」，XEAOS 帮你把整个公司跑成「AI 化组织」。",
      targetKeywords: ["Coze和XEAOS对比", "AI智能体平台推荐"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-10",
      question: "飞书 AI / 钉钉 AI 助手和 XEAOS 的区别？",
      answer: "飞书/钉钉的 AI 助手是「办公协作平台内置的 AI 能力」，解决沟通、会议、文档效率；XEAOS 是独立的「企业 AI 操作系统」，面向获客与增长——内容生产、多平台分发、AI 可见性监测、线索归集全链路。协作工具让内部更高效，XEAOS 让外部更赚钱，两者可以互补（XEAOS 内容也能发到飞书/钉钉生态）。",
      targetKeywords: ["飞书AI和XEAOS区别", "钉钉AI助手能做什么"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
    {
      id: "seed-faq-11",
      question: "Notion AI 和 Obsidian 知识库哪个更适合企业？",
      answer: "Notion AI 胜在开箱即用、团队协作模板丰富；Obsidian 胜在数据完全本地/私有、Markdown 格式开放、可编程（API/插件）、适合作为「企业唯一知识源」。XEAOS 选择 Obsidian 正是因为它满足 Rule 01（唯一知识源+可追溯+私有化），且 Git 同步可回滚。看重数据主权选 Obsidian 系，看重易用性选 Notion。",
      targetKeywords: ["Notion AI和Obsidian对比", "企业知识库搭建工具"],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    },
  ],
};

/** 信源类型元数据：标签 + 颜色 + 图标 + 说明 */
const SOURCE_TYPE_META: Record<SourceType, { label: string; color: string; icon: typeof Globe; desc: string }> = {
  website: { label: "官网", color: "#3B82F6", icon: Globe, desc: "企业官方网站" },
  wechat_official: { label: "官方公众号", color: "#10B981", icon: MessageSquare, desc: "微信公众号" },
  video_account: { label: "视频号", color: "#EF4444", icon: Radio, desc: "微信视频号 / 抖音号" },
  blue_v: { label: "企业蓝V", color: "#06B6D4", icon: Award, desc: "微博/抖音蓝 V 认证账号" },
  landing_page: { label: "落地页", color: "#8B5CF6", icon: Link2, desc: "营销落地页 / 活动页" },
  judicial: { label: "司法公示", color: "#F59E0B", icon: Building2, desc: "工商/司法公示信息" },
  media_report: { label: "行业媒体", color: "#EC4899", icon: FileText, desc: "行业媒体报道" },
};

/** 信源状态元数据 */
const SOURCE_STATUS_META: Record<SourceStatus, { label: string; color: string }> = {
  verified: { label: "已收录", color: "#10B981" },
  pending: { label: "待校验", color: "#F59E0B" },
  rejected: { label: "未收录", color: "#EF4444" },
};

/** 四级关键词元数据：标签 + 颜色 + 目标数量 + 用途说明 */
const KEYWORD_TIER_META: Record<KeywordTier, { label: string; color: string; targetCount: string; desc: string; icon: typeof Target }> = {
  brand_core: { label: "品牌核心词", color: "#A855F7", targetCount: "5-8个", desc: "守住品牌专属 AI 问答入口", icon: Award },
  business_precise: { label: "业务精准词", color: "#3B82F6", targetCount: "20-30个", desc: "承接精准意向客户搜索", icon: Crosshair },
  industry_longtail: { label: "行业痛点长尾词", color: "#10B981", targetCount: "50-80个", desc: "收割自然搜索泛流量", icon: TrendingUp },
  competitor: { label: "竞品对标词", color: "#F59E0B", targetCount: "10-15个", desc: "抢占竞品溢出流量", icon: Swords },
};

/** 关键词状态元数据 */
const KEYWORD_STATUS_META: Record<KeywordStatus, { label: string; color: string }> = {
  active: { label: "运营中", color: "#3B82F6" },
  ranking: { label: "已上榜", color: "#10B981" },
  pending: { label: "待优化", color: "#F59E0B" },
};

/** 4 大 AI 平台配置（豆包/文心一言/通义千问/DeepSeek） */
const AI_PLATFORMS = [
  { key: "doubao" as const, name: "豆包", color: "#3B82F6", short: "豆" },
  { key: "ernie" as const, name: "文心一言", color: "#EF4444", short: "文" },
  { key: "qwen" as const, name: "通义千问", color: "#8B5CF6", short: "通" },
  { key: "deepseek" as const, name: "DeepSeek", color: "#10B981", short: "D" },
];

/** 情感倾向元数据 */
const SENTIMENT_META: Record<AiMentionStat["sentiment"], { label: string; color: string }> = {
  positive: { label: "正面", color: "#10B981" },
  neutral: { label: "中性", color: "#9CA3AF" },
  negative: { label: "负面", color: "#EF4444" },
};

/** v6.1.17 地域快捷选项：湖南 14 地级市 + 全国（远程部署），GEO 地域输入用 */
const HUNAN_REGIONS: string[] = [
  "长沙", "株洲", "湘潭", "衡阳", "邵阳", "岳阳", "常德", "张家界",
  "益阳", "郴州", "永州", "怀化", "娄底", "湘西土家族苗族自治州",
];
const REGION_QUICK_OPTIONS: Array<{ label: string; region: string; serviceArea: string }> = [
  { label: "全国", region: "全国（远程部署）", serviceArea: "全国企业远程部署 AI 系统与内容运营" },
  ...HUNAN_REGIONS.map((r) => ({ label: r, region: r, serviceArea: `${r}本地企业数字化与 AI 运营服务` })),
];

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
const genId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** 格式化时间戳为可读字符串 */
const formatTime = (ts: string): string => {
  try {
    const d = new Date(ts);
    return d.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return ts;
  }
};

/** 从 localStorage 加载持久化数据 */
const loadState = (): GeoInputState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return SEED_STATE; // v5.2：首次打开自动预置小舍科技种子信源（白皮书要求，开箱可启动获客）
    const parsed = JSON.parse(raw) as Partial<GeoInputState>;
    // 合并默认值，避免旧数据缺字段导致崩溃
    const state: GeoInputState = {
      sources: Array.isArray(parsed.sources) ? parsed.sources : [],
      geoLocations: Array.isArray(parsed.geoLocations) ? parsed.geoLocations : [],
      brandPool: { ...EMPTY_STATE.brandPool, ...(parsed.brandPool || {}) },
      keywords: Array.isArray(parsed.keywords) ? parsed.keywords : [],
      faqs: Array.isArray(parsed.faqs) ? parsed.faqs : [],
    };
    // v5.2 种子增强合并：已有数据时把种子信源/品牌词/关键词/FAQ 中缺失的补进来
    // （用户不会因为已有旧数据而丢失新录入的小舍科技信源）
    const seedIds = new Set(SEED_STATE.sources.map((s) => s.id));
    for (const s of SEED_STATE.sources) {
      if (!state.sources.some((x) => x.id === s.id)) state.sources.push(s);
    }
    // v6.1.17c 旧数据自动修正：司法公示信源标题若还是旧名"小舍科技"→ 原位更新为工商注册全称
    state.sources = state.sources.map((s) => {
      if (s.type === "judicial" && s.title && s.title.includes("小舍科技") && !s.title.includes("湖南")) {
        return { ...s, title: "企业信息平台：湖南小舍科技有限公司（企查查/天眼查工商确权）" };
      }
      return s;
    });
    const seedKw = new Set(SEED_STATE.keywords.map((k) => k.keyword));
    for (const k of SEED_STATE.keywords) {
      if (!state.keywords.some((x) => x.keyword === k.keyword)) state.keywords.push(k);
    }
    const seedFaq = new Set(SEED_STATE.faqs.map((f) => f.question));
    for (const f of SEED_STATE.faqs) {
      if (!state.faqs.some((x) => x.question === f.question)) state.faqs.push(f);
    }
    // 品牌词池合并（去重）
    for (const p of SEED_STATE.brandPool.productNames) {
      if (!state.brandPool.productNames.includes(p)) state.brandPool.productNames.push(p);
    }
    if (state.brandPool.brandFullName !== SEED_STATE.brandPool.brandFullName && !state.brandPool.brandFullName) {
      state.brandPool.brandFullName = SEED_STATE.brandPool.brandFullName;
    }
    if (state.brandPool.brandShortName !== SEED_STATE.brandPool.brandShortName && !state.brandPool.brandShortName) {
      state.brandPool.brandShortName = SEED_STATE.brandPool.brandShortName;
    }
    for (const f of SEED_STATE.brandPool.founderIpNames) {
      if (!state.brandPool.founderIpNames.includes(f)) state.brandPool.founderIpNames.push(f);
    }
    return state;
  } catch {
    return SEED_STATE;
  }
};

/** 保存数据到 localStorage */
const saveState = (state: GeoInputState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn("[GeoInputEnhanced] localStorage 写入失败:", err);
  }
};

// ============================================
// 主组件
// ============================================

export default function GeoInputEnhanced() {
  // ---------- 状态 ----------
  const [state, setState] = useState<GeoInputState>(EMPTY_STATE);
  const [activeTab, setActiveTab] = useState<"sources" | "keywords" | "faq">("sources");
  const [obsidianQuery, setObsidianQuery] = useState("");
  const [obsidianResults, setObsidianResults] = useState<ObsidianSearchResult[]>([]);
  const [obsidianLoading, setObsidianLoading] = useState(false);
  const [obsidianError, setObsidianError] = useState<string | null>(null);
  const [obsidianSearched, setObsidianSearched] = useState(false);

  // 初始化：加载 localStorage 数据
  useEffect(() => {
    setState(loadState());
  }, []);

  // 数据变更：自动持久化
  useEffect(() => {
    saveState(state);
  }, [state]);

  // ---------- Obsidian 知识库检索 ----------
  // 调用 RuntimeManager.query('knowledge', { type: 'search', query })
  // 双层响应解包：外层 { success, data } → 内层 { success, data }
  const handleObsidianSearch = useCallback(async () => {
    const q = obsidianQuery.trim();
    if (!q) return;
    setObsidianLoading(true);
    setObsidianError(null);
    setObsidianSearched(false);
    setObsidianResults([]);
    try {
      // 等待 RuntimeManager 初始化（最多 6 秒）
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!RuntimeManager.isInitialized()) {
        setObsidianError("RuntimeManager 尚未初始化，无法检索 Obsidian 知识库");
        return;
      }
      // 按任务规范调用，同时附带 options 以兼容 KnowledgeRuntime 实际入参
      const resp = await (RuntimeManager as any).query("knowledge", {
        type: "search",
        query: q,
        options: { keyword: q, limit: 20 },
      });
      const inner = resp?.data as any;
      const data = inner?.success ? inner.data : inner;
      let results: any[] = [];
      if (Array.isArray(data)) {
        results = data;
      } else if (data && Array.isArray(data.results)) {
        results = data.results;
      } else if (data && Array.isArray(data.items)) {
        results = data.items;
      }
      setObsidianResults(
        results.slice(0, 20).map((r: any) => ({
          title: r.title || r.name || r.fileName || "未命名",
          path: r.path || r.folderName,
          summary: r.summary || (r.content ? String(r.content).slice(0, 120) : ""),
          score: r.score,
        }))
      );
    } catch (err: any) {
      setObsidianError(err?.message || String(err));
    } finally {
      setObsidianLoading(false);
      setObsidianSearched(true);
    }
  }, [obsidianQuery]);

  // ---------- 信源模块：增删改 ----------
  const handleAddSource = (source: Omit<BrandSource, "id" | "submittedAt" | "status">) => {
    const newSource: BrandSource = {
      ...source,
      id: genId(),
      status: "pending",
      submittedAt: new Date().toISOString(),
    };
    setState((s) => ({ ...s, sources: [newSource, ...s.sources] }));
  };
  const handleDeleteSource = (id: string) => {
    setState((s) => ({ ...s, sources: s.sources.filter((x) => x.id !== id) }));
  };
  const handleToggleAiIndex = (id: string, aiKey: "doubao" | "ernie" | "qwen" | "deepseek") => {
    setState((s) => ({
      ...s,
      sources: s.sources.map((x) =>
        x.id === id
          ? { ...x, aiIndexed: { ...(x.aiIndexed || {}), [aiKey]: !x.aiIndexed?.[aiKey] } }
          : x
      ),
    }));
  };

  // v6.1.15 信源校验状态切换（待校验 ↔ 已收录）：确认该信源真实存在后标记"已收录"
  const handleToggleSourceStatus = (id: string) => {
    setState((s) => ({
      ...s,
      sources: s.sources.map((x) =>
        x.id === id
          ? { ...x, status: x.status === "verified" ? "pending" : "verified", verifiedAt: x.status === "verified" ? undefined : new Date().toISOString() }
          : x
      ),
    }));
  };

  // ---------- 地域 GEO 模块 ----------
  const handleAddGeoLocation = (loc: Omit<GeoLocation, "id">) => {
    setState((s) => ({ ...s, geoLocations: [...s.geoLocations, { ...loc, id: genId() }] }));
  };
  const handleDeleteGeoLocation = (id: string) => {
    setState((s) => ({ ...s, geoLocations: s.geoLocations.filter((x) => x.id !== id) }));
  };

  // ---------- 品牌词监控池 ----------
  const handleUpdateBrandPool = (patch: Partial<BrandKeywordPool>) => {
    setState((s) => ({ ...s, brandPool: { ...s.brandPool, ...patch } }));
  };
  const handleAddMentionStat = (stat: Omit<AiMentionStat, "lastChecked">) => {
    setState((s) => ({
      ...s,
      brandPool: {
        ...s.brandPool,
        aiMentionStats: [
          ...s.brandPool.aiMentionStats!,
          { ...stat, lastChecked: new Date().toISOString() },
        ],
      },
    }));
  };
  const handleDeleteMentionStat = (keyword: string) => {
    setState((s) => ({
      ...s,
      brandPool: {
        ...s.brandPool,
        aiMentionStats: s.brandPool.aiMentionStats!.filter((x) => x.keyword !== keyword),
      },
    }));
  };

  // ---------- 关键词模块 ----------
  const handleAddKeyword = (kw: Omit<Keyword, "id">) => {
    setState((s) => ({ ...s, keywords: [...s.keywords, { ...kw, id: genId() }] }));
  };
  const handleDeleteKeyword = (id: string) => {
    setState((s) => ({ ...s, keywords: s.keywords.filter((x) => x.id !== id) }));
  };
  const handleUpdateKeyword = (id: string, patch: Partial<Keyword>) => {
    setState((s) => ({
      ...s,
      keywords: s.keywords.map((x) => (x.id === id ? { ...x, ...patch } : x)),
    }));
  };

  // ---------- FAQ 模块 ----------
  const handleAddFaq = (faq: Omit<GeoFaq, "id">) => {
    setState((s) => ({ ...s, faqs: [...s.faqs, { ...faq, id: genId() }] }));
  };
  const handleDeleteFaq = (id: string) => {
    setState((s) => ({ ...s, faqs: s.faqs.filter((x) => x.id !== id) }));
  };
  const handleUpdateFaq = (id: string, patch: Partial<GeoFaq>) => {
    setState((s) => ({ ...s, faqs: s.faqs.map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
  };
  const handleToggleFaqDeploy = (id: string, aiKey: "doubao" | "ernie" | "qwen" | "deepseek") => {
    setState((s) => ({
      ...s,
      faqs: s.faqs.map((x) =>
        x.id === id
          ? { ...x, deployedToAi: { ...x.deployedToAi, [aiKey]: !x.deployedToAi[aiKey] } }
          : x
      ),
    }));
  };

  // 将 Obsidian 检索结果一键导入为 FAQ 草稿
  const handleImportFromObsidian = (result: ObsidianSearchResult) => {
    handleAddFaq({
      question: result.title,
      answer: result.summary || "",
      contactInfo: "",
      landingUrl: "",
      targetKeywords: [],
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
      sourceFromObsidian: result.path,
    });
  };

  // ---------- 统计 ----------
  const tierCounts = useMemo(() => {
    const counts: Record<KeywordTier, number> = { brand_core: 0, business_precise: 0, industry_longtail: 0, competitor: 0 };
    state.keywords.forEach((k) => { counts[k.tier]++; });
    return counts;
  }, [state.keywords]);

  // 各级关键词目标数量（取范围上限）
  const tierTargetMax: Record<KeywordTier, number> = { brand_core: 8, business_precise: 30, industry_longtail: 80, competitor: 15 };

  // ============================================
  // 渲染
  // ============================================

  return (
    <div className="rounded-lg border border-blue-500/20 bg-gradient-to-br from-blue-500/[0.04] to-cyan-500/[0.02] p-3 backdrop-blur-sm">
      {/* ===== 头部 ===== */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <Globe size={14} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white flex items-center gap-1.5">
              GEO 输入层 · 大模型品牌占位系统
              <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/20">
                第2层改造
              </span>
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              信源入库 · 四级关键词 · AI 问答预埋
              <span className="text-blue-400 ml-1">· localStorage 持久化 · Obsidian 实时检索</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[9px] font-mono text-gray-500">
          <span className="flex items-center gap-1">
            <Database size={9} className="text-blue-500" />
            信源 {state.sources.length}
          </span>
          <span className="flex items-center gap-1">
            <Target size={9} className="text-cyan-500" />
            词 {state.keywords.length}
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare size={9} className="text-emerald-500" />
            FAQ {state.faqs.length}
          </span>
        </div>
      </div>

      {/* ===== Tab 切换 ===== */}
      <div className="flex items-center gap-1 mb-3 flex-wrap">
        {([
          { id: "sources" as const, label: "信源入库", icon: Database, color: "#3B82F6", count: state.sources.length },
          { id: "keywords" as const, label: "关键词分层", icon: Layers, color: "#A855F7", count: state.keywords.length },
          { id: "faq" as const, label: "AI 问答预埋", icon: Brain, color: "#10B981", count: state.faqs.length },
        ]).map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono transition-all border ${
                active
                  ? "text-white bg-white/[0.04] border-white/[0.06]"
                  : "text-gray-500 hover:text-gray-300 border-transparent hover:bg-white/[0.02]"
              }`}
              style={active ? { color: tab.color, borderColor: `${tab.color}40` } : {}}
            >
              <Icon size={11} />
              {tab.label}
              <span className="ml-0.5 text-[9px] opacity-60">({tab.count})</span>
            </button>
          );
        })}
      </div>

      {/* ===== 内容区（动画切换） ===== */}
      <div className="max-h-[560px] overflow-y-auto pr-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
          >
            {/* ==================== 模块1：信源入库 ==================== */}
            {activeTab === "sources" && (
              <SourcesModule
                state={state}
                onAddSource={handleAddSource}
                onDeleteSource={handleDeleteSource}
                onToggleAiIndex={handleToggleAiIndex}
                onToggleSourceStatus={handleToggleSourceStatus}
                onAddGeoLocation={handleAddGeoLocation}
                onDeleteGeoLocation={handleDeleteGeoLocation}
                onUpdateBrandPool={handleUpdateBrandPool}
                onAddMentionStat={handleAddMentionStat}
                onDeleteMentionStat={handleDeleteMentionStat}
              />
            )}

            {/* ==================== 模块2：关键词分层 ==================== */}
            {activeTab === "keywords" && (
              <KeywordsModule
                state={state}
                tierCounts={tierCounts}
                tierTargetMax={tierTargetMax}
                onAddKeyword={handleAddKeyword}
                onDeleteKeyword={handleDeleteKeyword}
                onUpdateKeyword={handleUpdateKeyword}
              />
            )}

            {/* ==================== 模块3：AI 问答预埋 ==================== */}
            {activeTab === "faq" && (
              <FaqModule
                state={state}
                obsidianQuery={obsidianQuery}
                setObsidianQuery={setObsidianQuery}
                obsidianResults={obsidianResults}
                obsidianLoading={obsidianLoading}
                obsidianError={obsidianError}
                obsidianSearched={obsidianSearched}
                onObsidianSearch={handleObsidianSearch}
                onImportFromObsidian={handleImportFromObsidian}
                onAddFaq={handleAddFaq}
                onDeleteFaq={handleDeleteFaq}
                onUpdateFaq={handleUpdateFaq}
                onToggleFaqDeploy={handleToggleFaqDeploy}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ===== 底部说明 ===== */}
      <div className="mt-3 pt-2 border-t border-white/[0.06] flex items-center justify-between text-[9px] font-mono text-gray-600">
        <span className="flex items-center gap-1">
          <Brain size={9} className="text-blue-600" />
          数据来源：localStorage（用户录入）+ Obsidian 知识库（KnowledgeRuntime 检索）
        </span>
        <span className="text-gray-700">GEO+DSO 改造 v2.0 · 第 9.1 节</span>
      </div>
    </div>
  );
}

// ============================================
// 子组件：模块1 - 信源入库
// ============================================
function SourcesModule(props: {
  state: GeoInputState;
  onAddSource: (s: Omit<BrandSource, "id" | "submittedAt" | "status">) => void;
  onDeleteSource: (id: string) => void;
  onToggleAiIndex: (id: string, ai: "doubao" | "ernie" | "qwen" | "deepseek") => void;
  onToggleSourceStatus: (id: string) => void;
  onAddGeoLocation: (l: Omit<GeoLocation, "id">) => void;
  onDeleteGeoLocation: (id: string) => void;
  onUpdateBrandPool: (patch: Partial<BrandKeywordPool>) => void;
  onAddMentionStat: (s: Omit<AiMentionStat, "lastChecked">) => void;
  onDeleteMentionStat: (keyword: string) => void;
}) {
  const { state } = props;
  const [collapsed, setCollapsed] = useState({ sources: false, geo: false, brand: false, mention: false });

  // 信源录入表单
  const [srcType, setSrcType] = useState<SourceType>("website");
  const [srcUrl, setSrcUrl] = useState("");
  const [srcTitle, setSrcTitle] = useState("");

  // 地域录入表单
  const [geoRegion, setGeoRegion] = useState("长沙");
  const [geoArea, setGeoArea] = useState("");
  const [geoDistricts, setGeoDistricts] = useState("");

  // 品牌词编辑
  const [productName, setProductName] = useState("");
  const [founderName, setFounderName] = useState("");

  // 提及监控录入
  const [mentionKw, setMentionKw] = useState("");
  const [mentionDoubao, setMentionDoubao] = useState(0);
  const [mentionErnie, setMentionErnie] = useState(0);
  const [mentionQwen, setMentionQwen] = useState(0);
  const [mentionDeepseek, setMentionDeepseek] = useState(0);
  const [mentionSentiment, setMentionSentiment] = useState<AiMentionStat["sentiment"]>("neutral");
  // v6.1.17 一键诊断（AI 提及监控）：从品牌词池取关键词，模拟 4 大 AI 平台提问并自动生成监控记录
  const [mentionDiagRunning, setMentionDiagRunning] = useState(false);
  const [mentionDiagProgress, setMentionDiagProgress] = useState("");

  const runMentionDiagnosis = async () => {
    const pool = state.brandPool;
    // 取品牌词池关键词（品牌/产品/创始人 IP 去重，最多 6 个，控制耗时）
    const kws = Array.from(new Set([
      pool.brandFullName, pool.brandShortName,
      ...pool.productNames, ...pool.founderIpNames,
    ].filter(Boolean))).slice(0, 6);
    if (kws.length === 0) return;
    setMentionDiagRunning(true);

    // DeepSeek API Key（模拟引擎）
    let apiKey: string | undefined;
    try { apiKey = apiConfig.getApiKey('deepseek'); } catch { /* ignore */ }
    if (!apiKey) {
      try { const env = (import.meta as any).env; if (env?.VITE_DEEPSEEK_API_KEY) apiKey = env.VITE_DEEPSEEK_API_KEY; } catch { /* ignore */ }
    }
    if (!apiKey) {
      setMentionDiagProgress("未配置 DeepSeek API Key（设置页或 .env）");
      setMentionDiagRunning(false);
      return;
    }

    const PLATFORM_KEYS: Array<{ key: keyof Omit<AiMentionStat, "keyword" | "sentiment" | "lastChecked">; name: string; persona: string }> = [
      { key: "doubaoMentions", name: "豆包", persona: "你是豆包，字节跳动 AI 助手，回答口语化简洁。" },
      { key: "ernieMentions", name: "文心一言", persona: "你是文心一言，百度 AI 助手，回答稳重条理。" },
      { key: "qwenMentions", name: "通义千问", persona: "你是通义千问，阿里 AI 助手，回答专业完整。" },
      { key: "deepseekMentions", name: "DeepSeek", persona: "你是 DeepSeek，回答简洁直接实事求是。" },
    ];

    for (const kw of kws) {
      setMentionDiagProgress(`正在诊断「${kw}」…`);
      try {
        // 单次调用让 LLM 分别以 4 平台口吻回答，一次拿到 4 个答案（比 4 次调用快 4 倍）
        const resp = await fetch('/deepseek-api/v1/chat/completions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
          body: JSON.stringify({
            model: 'deepseek-chat',
            messages: [
              { role: 'system', content: '你是 AI 搜索模拟引擎。请分别以豆包、文心一言、通义千问、DeepSeek 四个 AI 助手的口吻，各自回答下面的用户问题。严格返回 JSON：{"doubao":"豆包的回答","ernie":"文心一言的回答","qwen":"通义千问的回答","deepseek":"DeepSeek的回答"}，每个回答 80-200 字，客观中立，可提及市面真实存在的产品或品牌，不确定就说不确定。' },
              { role: 'user', content: `推荐一下「${kw}」相关的工具或服务？` },
            ],
            temperature: 0.4,
            max_tokens: 1200,
            stream: false,
          }),
        });
        if (!resp.ok) {
          setMentionDiagProgress(`「${kw}」诊断失败（HTTP ${resp.status}），跳过`);
          continue;
        }
        const data = await resp.json();
        // 兼容两种响应格式：vite 直达 OpenAI 格式 {choices} / 生产 server.py 代理 {success,content}
        const content = data?.choices?.[0]?.message?.content || data?.content || '';
        // 解析 JSON（兼容带代码块/说明）
        let parsed: Record<string, string> = {};
        const m = content.match(/\{[\s\S]*\}/);
        if (m) { try { parsed = JSON.parse(m[0]); } catch { /* 忽略 */ } }
        // 每个平台回答是否提及该关键词 → 0/1
        const mentionOf = (text: string) => (text && text.includes(kw) ? 1 : 0);
        const answers = [parsed.doubao || '', parsed.ernie || '', parsed.qwen || '', parsed.deepseek || ''];
        const allText = answers.join(' ');
        const sentiment: AiMentionStat["sentiment"] = /不推荐|不建议|质量差|坑/.test(allText) ? "negative" : /推荐|不错|好用|值得/.test(allText) ? "positive" : "neutral";
        props.onAddMentionStat({
          keyword: kw,
          doubaoMentions: mentionOf(parsed.doubao || ''),
          ernieMentions: mentionOf(parsed.ernie || ''),
          qwenMentions: mentionOf(parsed.qwen || ''),
          deepseekMentions: mentionOf(parsed.deepseek || ''),
          sentiment,
        });
        setMentionDiagProgress(`「${kw}」完成`);
      } catch (err: any) {
        setMentionDiagProgress(`「${kw}」异常：${err?.message || err}`);
      }
    }
    setMentionDiagRunning(false);
    setMentionDiagProgress("诊断完成：已自动写入 AI 提及监控（模拟数据，真实排名请到各平台亲自核实）");
  };

  return (
    <div className="space-y-3">
      {/* ----- 信源列表 ----- */}
      <CollapsibleCard
        title="结构化信源入库"
        icon={Database}
        color="#3B82F6"
        count={state.sources.length}
        collapsed={collapsed.sources}
        onToggle={() => setCollapsed((c) => ({ ...c, sources: !c.sources }))}
      >
        {/* 添加信源表单 */}
        <div className="mb-2 p-2 rounded border border-blue-500/15 bg-blue-500/[0.02]">
          <div className="text-[9px] font-mono text-blue-300 mb-1.5 uppercase tracking-wider">添加权威信源</div>
          <div className="grid grid-cols-12 gap-1.5 mb-1.5">
            <select
              value={srcType}
              onChange={(e) => setSrcType(e.target.value as SourceType)}
              className="col-span-4 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-blue-500/40"
            >
              {(Object.keys(SOURCE_TYPE_META) as SourceType[]).map((t) => (
                <option key={t} value={t} className="bg-gray-900">
                  {SOURCE_TYPE_META[t].label}
                </option>
              ))}
            </select>
            <input
              value={srcUrl}
              onChange={(e) => setSrcUrl(e.target.value)}
              placeholder="信源 URL（如 https://...）"
              className="col-span-5 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-blue-500/40"
            />
            <input
              value={srcTitle}
              onChange={(e) => setSrcTitle(e.target.value)}
              placeholder="标题"
              className="col-span-3 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-blue-500/40"
            />
          </div>
          <button
            onClick={() => {
              if (!srcUrl.trim()) return;
              props.onAddSource({ type: srcType, url: srcUrl.trim(), title: srcTitle.trim() || SOURCE_TYPE_META[srcType].label });
              setSrcUrl("");
              setSrcTitle("");
            }}
            disabled={!srcUrl.trim()}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={10} />
            提交信源
          </button>
        </div>

        {/* 信源列表 / 空状态 */}
        {state.sources.length === 0 ? (
          <EmptyState icon={Inbox} text="暂无信源数据。请录入企业权威信源（官网/公众号/视频号/蓝V/落地页/司法/媒体）。" />
        ) : (
          <div className="space-y-1">
            {state.sources.map((src) => {
              const meta = SOURCE_TYPE_META[src.type];
              const statusMeta = SOURCE_STATUS_META[src.status];
              const Icon = meta.icon;
              return (
                <div key={src.id} className="p-1.5 rounded border border-white/[0.04] bg-white/[0.01]">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Icon size={11} style={{ color: meta.color }} />
                      <span className="text-[10px] text-gray-200 truncate font-medium">{src.title || "未命名"}</span>
                      <span className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0" style={{ background: `${meta.color}20`, color: meta.color }}>
                        {meta.label}
                      </span>
                      {/* v6.1.15 信源校验状态可点击切换：确认真实存在 → 标记"已收录" */}
                      <button
                        onClick={() => props.onToggleSourceStatus(src.id)}
                        className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0 border transition-colors cursor-pointer hover:opacity-80"
                        style={{ background: `${statusMeta.color}20`, color: statusMeta.color, borderColor: `${statusMeta.color}40` }}
                        title={`当前：${statusMeta.label} · 点击切换为${src.status === "verified" ? "待校验" : "已收录"}（确认该信源真实存在后可标记已收录，作为 GEO 可引用信源）`}
                      >
                        ● {statusMeta.label} {src.status === "pending" ? "· 点此收录" : ""}
                      </button>
                    </div>
                    <button onClick={() => props.onDeleteSource(src.id)} className="text-gray-600 hover:text-red-400 flex-shrink-0" title="删除">
                      <Trash2 size={11} />
                    </button>
                  </div>
                  <div className="flex items-center gap-1 text-[9px] font-mono text-blue-400 mb-1 truncate">
                    <Link2 size={9} />
                    <span className="truncate">{src.url}</span>
                  </div>
                  {/* AI 收录状态：4 大平台 */}
                  <div className="flex items-center gap-1">
                    <span className="text-[8px] font-mono text-gray-600 mr-0.5">AI收录：</span>
                    {AI_PLATFORMS.map((ai) => {
                      const indexed = src.aiIndexed?.[ai.key];
                      return (
                        <button
                          key={ai.key}
                          onClick={() => props.onToggleAiIndex(src.id, ai.key)}
                          className={`text-[8px] font-mono px-1 py-0.5 rounded border transition-all ${
                            indexed
                              ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                              : "bg-white/[0.02] text-gray-600 border-white/[0.06] hover:border-white/[0.12]"
                          }`}
                          title={`${ai.name} ${indexed ? "已收录" : "未收录"}，点击切换`}
                        >
                          {ai.short}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[8px] font-mono text-gray-600 mt-0.5">提交于 {formatTime(src.submittedAt)}</div>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleCard>

      {/* ----- 地域 GEO 信息 ----- */}
      <CollapsibleCard
        title="地域 GEO 信息"
        icon={MapPin}
        color="#10B981"
        count={state.geoLocations.length}
        collapsed={collapsed.geo}
        onToggle={() => setCollapsed((c) => ({ ...c, geo: !c.geo }))}
      >
        <div className="mb-2 p-2 rounded border border-emerald-500/15 bg-emerald-500/[0.02]">
          <div className="text-[9px] font-mono text-emerald-300 mb-1.5 uppercase tracking-wider">录入地域信息</div>
          {/* v6.1.17 地域快捷选项：全国（远程部署）+ 湖南 14 地级市，点击即填 */}
          <div className="flex items-center gap-1 flex-wrap mb-1.5">
            <span className="text-[8px] font-mono text-gray-600">快捷：</span>
            {REGION_QUICK_OPTIONS.map((opt) => (
              <button
                key={opt.region}
                onClick={() => {
                  setGeoRegion(opt.region);
                  setGeoArea(opt.serviceArea);
                }}
                className={`px-1.5 py-0.5 rounded text-[8px] font-mono border transition-colors ${
                  geoRegion === opt.region
                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                    : "text-gray-500 hover:text-gray-300 border-white/[0.06] hover:border-emerald-500/30"
                }`}
                title={`填入：${opt.region} · ${opt.serviceArea}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-12 gap-1.5 mb-1.5">
            <input
              value={geoRegion}
              onChange={(e) => setGeoRegion(e.target.value)}
              placeholder="服务区域（如 长沙）"
              className="col-span-3 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-emerald-500/40"
            />
            <input
              value={geoArea}
              onChange={(e) => setGeoArea(e.target.value)}
              placeholder="服务范围描述（如 五区可上门）"
              className="col-span-5 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-emerald-500/40"
            />
            <input
              value={geoDistricts}
              onChange={(e) => setGeoDistricts(e.target.value)}
              placeholder="商圈（逗号分隔）"
              className="col-span-4 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-emerald-500/40"
            />
          </div>
          <button
            onClick={() => {
              if (!geoRegion.trim()) return;
              props.onAddGeoLocation({
                region: geoRegion.trim(),
                serviceArea: geoArea.trim(),
                businessDistricts: geoDistricts.split(/[,，]/).map((x) => x.trim()).filter(Boolean),
                offlinePoints: [],
              });
              setGeoArea("");
              setGeoDistricts("");
            }}
            disabled={!geoRegion.trim()}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded transition-colors disabled:opacity-40"
          >
            <Plus size={10} />
            添加地域
          </button>
        </div>
        {state.geoLocations.length === 0 ? (
          <EmptyState icon={MapPin} text="暂无地域信息。录入长沙本地服务范围、商圈、线下点位。" />
        ) : (
          <div className="space-y-1">
            {state.geoLocations.map((loc) => (
              <div key={loc.id} className="p-1.5 rounded border border-white/[0.04] bg-white/[0.01]">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-gray-200 font-medium flex items-center gap-1">
                    <MapPin size={10} className="text-emerald-400" />
                    {loc.region}
                  </span>
                  <button onClick={() => props.onDeleteGeoLocation(loc.id)} className="text-gray-600 hover:text-red-400">
                    <Trash2 size={11} />
                  </button>
                </div>
                {loc.serviceArea && <div className="text-[9px] text-gray-400 mb-1">{loc.serviceArea}</div>}
                {loc.businessDistricts.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {loc.businessDistricts.map((d) => (
                      <span key={d} className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-300">
                        {d}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CollapsibleCard>

      {/* ----- 品牌词监控池 ----- */}
      <CollapsibleCard
        title="品牌词监控池"
        icon={Tag}
        color="#A855F7"
        collapsed={collapsed.brand}
        onToggle={() => setCollapsed((c) => ({ ...c, brand: !c.brand }))}
      >
        {/* v6.1.17 应用官方推荐：品牌词 + 信源（合并去重，不覆盖用户已有数据） */}
        <div className="mb-1.5 p-1.5 rounded border border-purple-500/20 bg-purple-500/[0.03] flex items-center gap-2 flex-wrap">
          <span className="text-[8px] font-mono text-purple-300">品牌词/信源未更新？点此一键合并官方推荐（湖南小舍科技/企业AI操作系统/小舍李师兄/Andy哥 + 司法公示信源）：</span>
          <button
            onClick={() => {
              const cur = state.brandPool;
              const mergedProducts = Array.from(new Set([...(cur.productNames || []), ...SEED_STATE.brandPool.productNames]));
              const mergedFounders = Array.from(new Set([...(cur.founderIpNames || []), ...SEED_STATE.brandPool.founderIpNames]));
              props.onUpdateBrandPool({
                brandFullName: cur.brandFullName?.includes("科技") ? cur.brandFullName : SEED_STATE.brandPool.brandFullName,
                productNames: mergedProducts,
                founderIpNames: mergedFounders,
              });
              // v6.1.17b 同步更新司法公示信源标题为工商注册全称
              const judicial = SEED_STATE.sources.find((s) => s.type === "judicial");
              if (judicial && !state.sources.some((s) => s.type === "judicial" && (s.title?.includes("湖南小舍科技有限公司") || s.title?.includes("湖南小舍")))) {
                props.onAddSource({ type: "judicial", url: judicial.url, title: "企业信息平台：湖南小舍科技有限公司（企查查/天眼查工商确权）" });
              }
            }}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono text-white bg-purple-600/80 hover:bg-purple-600 transition-colors"
            title="合并官方推荐品牌词（湖南小舍科技有限公司/企业AI操作系统/小舍李师兄/Andy哥/xiaoshe 等）+ 司法公示信源（湖南小舍科技有限公司）"
          >
            <Zap size={9} />
            应用官方推荐（品牌词+信源）
          </button>
        </div>
        <div className="p-2 rounded border border-purple-500/15 bg-purple-500/[0.02] space-y-1.5">
          <div className="text-[9px] font-mono text-purple-300 mb-1 uppercase tracking-wider">品牌基础信息</div>
          <div className="grid grid-cols-2 gap-1.5">
            <input
              value={state.brandPool.brandFullName}
              onChange={(e) => props.onUpdateBrandPool({ brandFullName: e.target.value })}
              placeholder="品牌全称（如 湖南某某科技有限公司）"
              className="bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-purple-500/40"
            />
            <input
              value={state.brandPool.brandShortName}
              onChange={(e) => props.onUpdateBrandPool({ brandShortName: e.target.value })}
              placeholder="品牌简称（如 某某科技）"
              className="bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-purple-500/40"
            />
          </div>
          {/* 产品词 */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <input
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="产品词"
                className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-purple-500/40"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && productName.trim()) {
                    props.onUpdateBrandPool({ productNames: [...state.brandPool.productNames, productName.trim()] });
                    setProductName("");
                  }
                }}
              />
              <button
                onClick={() => {
                  if (!productName.trim()) return;
                  props.onUpdateBrandPool({ productNames: [...state.brandPool.productNames, productName.trim()] });
                  setProductName("");
                }}
                className="px-1.5 py-1 text-[10px] font-mono text-white bg-purple-600/70 hover:bg-purple-600 rounded"
              >
                <Plus size={10} />
              </button>
            </div>
            {state.brandPool.productNames.length === 0 ? (
              <div className="text-[8px] font-mono text-gray-600">无产品词</div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {state.brandPool.productNames.map((p, i) => (
                  <span key={i} className="flex items-center gap-1 text-[8px] font-mono px-1 py-0.5 rounded bg-purple-500/10 text-purple-300">
                    {p}
                    <button
                      onClick={() => props.onUpdateBrandPool({ productNames: state.brandPool.productNames.filter((_, j) => j !== i) })}
                      className="hover:text-red-400"
                    >
                      <X size={8} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* 创始人 IP 词 */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <input
                value={founderName}
                onChange={(e) => setFounderName(e.target.value)}
                placeholder="创始人 IP 词"
                className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-purple-500/40"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && founderName.trim()) {
                    props.onUpdateBrandPool({ founderIpNames: [...state.brandPool.founderIpNames, founderName.trim()] });
                    setFounderName("");
                  }
                }}
              />
              <button
                onClick={() => {
                  if (!founderName.trim()) return;
                  props.onUpdateBrandPool({ founderIpNames: [...state.brandPool.founderIpNames, founderName.trim()] });
                  setFounderName("");
                }}
                className="px-1.5 py-1 text-[10px] font-mono text-white bg-purple-600/70 hover:bg-purple-600 rounded"
              >
                <Plus size={10} />
              </button>
            </div>
            {state.brandPool.founderIpNames.length === 0 ? (
              <div className="text-[8px] font-mono text-gray-600">无创始人 IP 词</div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {state.brandPool.founderIpNames.map((p, i) => (
                  <span key={i} className="flex items-center gap-1 text-[8px] font-mono px-1 py-0.5 rounded bg-pink-500/10 text-pink-300">
                    {p}
                    <button
                      onClick={() => props.onUpdateBrandPool({ founderIpNames: state.brandPool.founderIpNames.filter((_, j) => j !== i) })}
                      className="hover:text-red-400"
                    >
                      <X size={8} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </CollapsibleCard>

      {/* ----- AI 提及监控仪表盘 ----- */}
      <CollapsibleCard
        title="AI 提及监控仪表盘"
        icon={Eye}
        color="#06B6D4"
        count={state.brandPool.aiMentionStats!.length}
        collapsed={collapsed.mention}
        onToggle={() => setCollapsed((c) => ({ ...c, mention: !c.mention }))}
      >
        <div className="mb-2 p-2 rounded border border-cyan-500/15 bg-cyan-500/[0.02]">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">录入 AI 提及监控</span>
            {/* v6.1.17 一键诊断：从品牌词池取关键词，模拟 4 大 AI 平台自动生成记录 */}
            <button
              onClick={runMentionDiagnosis}
              disabled={mentionDiagRunning}
              className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono text-white bg-cyan-600/80 hover:bg-cyan-600 transition-colors disabled:opacity-40"
              title="自动诊断品牌词池中前 6 个关键词在豆包/文心/通义/DeepSeek 的提及情况并写入监控（模拟数据，真实排名请到各平台核实）"
            >
              {mentionDiagRunning ? <Loader2 size={9} className="animate-spin" /> : <Zap size={9} />}
              {mentionDiagRunning ? "诊断中…" : "⚡ 一键诊断（自动）"}
            </button>
          </div>
          {mentionDiagProgress && (
            <div className="mb-1.5 text-[9px] font-mono text-cyan-300">{mentionDiagProgress}</div>
          )}
          <div className="grid grid-cols-12 gap-1 mb-1.5">
            <input
              value={mentionKw}
              onChange={(e) => setMentionKw(e.target.value)}
              placeholder="监控关键词（如 XEAOS / 企业AI操作系统）"
              title="填写你想监控的 AI 回答关键词：品牌词（XEAOS/小舍AI）或行业词（企业AI操作系统）。建议用品牌词池中的词；也可点上方「一键诊断」自动取品牌词池关键词"
              className="col-span-4 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-cyan-500/40"
            />
            <select
              value={mentionSentiment}
              onChange={(e) => setMentionSentiment(e.target.value as AiMentionStat["sentiment"])}
              className="col-span-3 bg-white/[0.03] border border-white/[0.06] rounded px-1 py-1 text-[9px] text-gray-200 font-mono outline-none"
            >
              <option value="positive" className="bg-gray-900">正面</option>
              <option value="neutral" className="bg-gray-900">中性</option>
              <option value="negative" className="bg-gray-900">负面</option>
            </select>
            <input
              type="number"
              value={mentionDoubao}
              onChange={(e) => setMentionDoubao(Number(e.target.value) || 0)}
              placeholder="豆包"
              className="col-span-1 bg-white/[0.03] border border-white/[0.06] rounded px-1 py-1 text-[9px] text-blue-300 font-mono text-center outline-none"
            />
            <input
              type="number"
              value={mentionErnie}
              onChange={(e) => setMentionErnie(Number(e.target.value) || 0)}
              placeholder="文心"
              className="col-span-1 bg-white/[0.03] border border-white/[0.06] rounded px-1 py-1 text-[9px] text-red-300 font-mono text-center outline-none"
            />
            <input
              type="number"
              value={mentionQwen}
              onChange={(e) => setMentionQwen(Number(e.target.value) || 0)}
              placeholder="千问"
              className="col-span-1 bg-white/[0.03] border border-white/[0.06] rounded px-1 py-1 text-[9px] text-purple-300 font-mono text-center outline-none"
            />
            <input
              type="number"
              value={mentionDeepseek}
              onChange={(e) => setMentionDeepseek(Number(e.target.value) || 0)}
              placeholder="DS"
              className="col-span-1 bg-white/[0.03] border border-white/[0.06] rounded px-1 py-1 text-[9px] text-emerald-300 font-mono text-center outline-none"
            />
          </div>
          <button
            onClick={() => {
              if (!mentionKw.trim()) return;
              props.onAddMentionStat({
                keyword: mentionKw.trim(),
                doubaoMentions: mentionDoubao,
                ernieMentions: mentionErnie,
                qwenMentions: mentionQwen,
                deepseekMentions: mentionDeepseek,
                sentiment: mentionSentiment,
              });
              setMentionKw("");
              setMentionDoubao(0);
              setMentionErnie(0);
              setMentionQwen(0);
              setMentionDeepseek(0);
            }}
            disabled={!mentionKw.trim()}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-cyan-600/80 hover:bg-cyan-600 rounded disabled:opacity-40"
          >
            <Plus size={10} />
            记录提及
          </button>
        </div>
        {state.brandPool.aiMentionStats!.length === 0 ? (
          <EmptyState icon={Eye} text="暂无 AI 提及监控数据。录入各大 AI 对品牌词的提及频次与情感倾向。" />
        ) : (
          <div className="space-y-1.5">
            {state.brandPool.aiMentionStats!.map((m) => {
              const total = m.doubaoMentions + m.ernieMentions + m.qwenMentions + m.deepseekMentions;
              const sent = SENTIMENT_META[m.sentiment];
              return (
                <div key={m.keyword} className="p-1.5 rounded border border-white/[0.04] bg-white/[0.01]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-gray-200 font-medium">{m.keyword}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-[8px] font-mono px-1 py-0.5 rounded" style={{ background: `${sent.color}20`, color: sent.color }}>
                        {sent.label}
                      </span>
                      <span className="text-[8px] font-mono text-gray-500">总{total}</span>
                      <button onClick={() => props.onDeleteMentionStat(m.keyword)} className="text-gray-600 hover:text-red-400">
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </div>
                  {/* 4 大 AI 提及柱状对比 */}
                  <div className="space-y-0.5">
                    {AI_PLATFORMS.map((ai) => {
                      const val = m[`${ai.key}Mentions` as keyof AiMentionStat] as number;
                      const pct = total > 0 ? (val / total) * 100 : 0;
                      return (
                        <div key={ai.key} className="flex items-center gap-1.5">
                          <span className="text-[8px] font-mono w-10" style={{ color: ai.color }}>{ai.name}</span>
                          <div className="flex-1 h-2 rounded bg-white/[0.03] overflow-hidden">
                            <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: ai.color }} />
                          </div>
                          <span className="text-[8px] font-mono text-gray-400 w-6 text-right">{val}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[8px] font-mono text-gray-600 mt-0.5">检测于 {formatTime(m.lastChecked)}</div>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleCard>
    </div>
  );
}

// ============================================
// 子组件：模块2 - 关键词分层
// ============================================
function KeywordsModule(props: {
  state: GeoInputState;
  tierCounts: Record<KeywordTier, number>;
  tierTargetMax: Record<KeywordTier, number>;
  onAddKeyword: (k: Omit<Keyword, "id">) => void;
  onDeleteKeyword: (id: string) => void;
  onUpdateKeyword: (id: string, patch: Partial<Keyword>) => void;
}) {
  const { state, tierCounts, tierTargetMax } = props;
  const [addTier, setAddTier] = useState<KeywordTier>("brand_core");
  const [addKw, setAddKw] = useState("");
  const [addTarget, setAddTarget] = useState(1);

  return (
    <div className="space-y-3">
      {/* 四级关键词体系概览卡片 */}
      <div className="grid grid-cols-2 gap-1.5">
        {(Object.keys(KEYWORD_TIER_META) as KeywordTier[]).map((tier) => {
          const meta = KEYWORD_TIER_META[tier];
          const Icon = meta.icon;
          const current = tierCounts[tier];
          const target = tierTargetMax[tier];
          const pct = Math.min(100, (current / target) * 100);
          const complete = current >= target;
          return (
            <div key={tier} className="p-2 rounded border bg-white/[0.02]" style={{ borderColor: `${meta.color}30` }}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <Icon size={12} style={{ color: meta.color }} />
                  <span className="text-[10px] font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                </div>
                {complete && <CheckCircle2 size={11} className="text-emerald-400" />}
              </div>
              <div className="text-[8px] font-mono text-gray-500 mb-1">{meta.desc}</div>
              {/* 进度条 */}
              <div className="flex items-center gap-1.5 mb-0.5">
                <div className="flex-1 h-1.5 rounded bg-white/[0.04] overflow-hidden">
                  <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: meta.color }} />
                </div>
                <span className="text-[9px] font-mono text-gray-300">
                  {current}<span className="text-gray-600">/{target}</span>
                </span>
              </div>
              <div className="text-[8px] font-mono text-gray-600">目标 {meta.targetCount}</div>
            </div>
          );
        })}
      </div>

      {/* 添加关键词 */}
      <div className="p-2 rounded border border-purple-500/15 bg-purple-500/[0.02]">
        <div className="text-[9px] font-mono text-purple-300 mb-1.5 uppercase tracking-wider">添加关键词</div>
        <div className="grid grid-cols-12 gap-1.5 mb-1.5">
          <select
            value={addTier}
            onChange={(e) => setAddTier(e.target.value as KeywordTier)}
            className="col-span-5 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-purple-500/40"
          >
            {(Object.keys(KEYWORD_TIER_META) as KeywordTier[]).map((t) => (
              <option key={t} value={t} className="bg-gray-900">
                {KEYWORD_TIER_META[t].label}（目标 {KEYWORD_TIER_META[t].targetCount}）
              </option>
            ))}
          </select>
          <input
            value={addKw}
            onChange={(e) => setAddKw(e.target.value)}
            placeholder="关键词"
            className="col-span-4 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-purple-500/40"
            onKeyDown={(e) => {
              if (e.key === "Enter" && addKw.trim()) {
                props.onAddKeyword({ tier: addTier, keyword: addKw.trim(), targetRank: addTarget, status: "pending" });
                setAddKw("");
              }
            }}
          />
          <input
            type="number"
            min={1}
            value={addTarget}
            onChange={(e) => setAddTarget(Math.max(1, Number(e.target.value) || 1))}
            placeholder="目标排名"
            className="col-span-2 bg-white/[0.03] border border-white/[0.06] rounded px-1 py-1 text-[9px] text-gray-200 font-mono text-center outline-none"
          />
          <button
            onClick={() => {
              if (!addKw.trim()) return;
              props.onAddKeyword({ tier: addTier, keyword: addKw.trim(), targetRank: addTarget, status: "pending" });
              setAddKw("");
            }}
            disabled={!addKw.trim()}
            className="col-span-1 flex items-center justify-center text-white bg-purple-600/80 hover:bg-purple-600 rounded disabled:opacity-40"
          >
            <Plus size={12} />
          </button>
        </div>
      </div>

      {/* 按层级分组的关键词列表 + 排名监控 */}
      {(Object.keys(KEYWORD_TIER_META) as KeywordTier[]).map((tier) => {
        const meta = KEYWORD_TIER_META[tier];
        const Icon = meta.icon;
        const list = state.keywords.filter((k) => k.tier === tier);
        if (list.length === 0) return null;
        return (
          <div key={tier} className="rounded border bg-white/[0.01]" style={{ borderColor: `${meta.color}25` }}>
            <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-white/[0.04]">
              <Icon size={11} style={{ color: meta.color }} />
              <span className="text-[10px] font-semibold" style={{ color: meta.color }}>{meta.label}</span>
              <span className="text-[8px] font-mono text-gray-500">{list.length} 个 · {meta.desc}</span>
            </div>
            <div className="p-1.5 space-y-1">
              {list.map((kw) => {
                const statusMeta = KEYWORD_STATUS_META[kw.status];
                const rankDiff = kw.currentRank != null && kw.targetRank
                  ? kw.currentRank <= kw.targetRank
                  : null;
                return (
                  <div key={kw.id} className="p-1.5 rounded border border-white/[0.04] bg-white/[0.02]">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[10px] text-gray-200 truncate">{kw.keyword}</span>
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0" style={{ background: `${statusMeta.color}20`, color: statusMeta.color }}>
                          {statusMeta.label}
                        </span>
                        {kw.difficulty != null && (
                          <span className={`text-[8px] font-mono px-1 py-0.5 rounded flex-shrink-0 ${
                            kw.difficulty >= 70 ? "bg-red-500/15 text-red-400" :
                            kw.difficulty >= 40 ? "bg-amber-500/15 text-amber-400" :
                            "bg-emerald-500/15 text-emerald-400"
                          }`} title="竞争难度 1-100">
                            难度 {kw.difficulty}
                          </span>
                        )}
                      </div>
                      <button onClick={() => props.onDeleteKeyword(kw.id)} className="text-gray-600 hover:text-red-400 flex-shrink-0">
                        <Trash2 size={10} />
                      </button>
                    </div>
                    {/* 排名监控 */}
                    <div className="grid grid-cols-3 gap-1.5 text-[9px] font-mono">
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-600">当前排名</span>
                        <input
                          type="number"
                          min={1}
                          value={kw.currentRank ?? ""}
                          onChange={(e) => props.onUpdateKeyword(kw.id, { currentRank: e.target.value ? Number(e.target.value) : undefined })}
                          className="bg-white/[0.03] border border-white/[0.06] rounded px-1.5 py-0.5 text-gray-200 outline-none focus:border-blue-500/40"
                        />
                      </label>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-600">目标排名</span>
                        <input
                          type="number"
                          min={1}
                          value={kw.targetRank}
                          onChange={(e) => props.onUpdateKeyword(kw.id, { targetRank: Math.max(1, Number(e.target.value) || 1) })}
                          className="bg-white/[0.03] border border-white/[0.06] rounded px-1.5 py-0.5 text-gray-200 outline-none focus:border-blue-500/40"
                        />
                      </label>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-gray-600">状态</span>
                        <select
                          value={kw.status}
                          onChange={(e) => props.onUpdateKeyword(kw.id, { status: e.target.value as KeywordStatus })}
                          className="bg-white/[0.03] border border-white/[0.06] rounded px-1 py-0.5 text-gray-200 outline-none"
                        >
                          <option value="pending" className="bg-gray-900">待优化</option>
                          <option value="active" className="bg-gray-900">运营中</option>
                          <option value="ranking" className="bg-gray-900">已上榜</option>
                        </select>
                      </label>
                    </div>
                    {/* 排名达成情况 */}
                    {kw.currentRank != null && (
                      <div className="mt-1 flex items-center gap-1.5 text-[8px] font-mono">
                        {rankDiff === true ? (
                          <span className="flex items-center gap-0.5 text-emerald-400">
                            <CheckCircle2 size={9} /> 已达成目标（#{kw.currentRank} ≤ #{kw.targetRank}）
                          </span>
                        ) : rankDiff === false ? (
                          <span className="flex items-center gap-0.5 text-amber-400">
                            <TrendingUp size={9} /> 还差 {kw.currentRank - kw.targetRank} 名（#{kw.currentRank} → #{kw.targetRank}）
                          </span>
                        ) : null}
                        {kw.searchVolume != null && (
                          <span className="text-blue-400 ml-auto">搜索量 {kw.searchVolume.toLocaleString()}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* 空状态 */}
      {state.keywords.length === 0 && (
        <EmptyState icon={Target} text="暂无关键词。请按四级体系录入关键词：品牌核心词 / 业务精准词 / 行业长尾词 / 竞品对标词。" />
      )}
    </div>
  );
}

// ============================================
// 子组件：模块3 - AI 问答预埋
// ============================================
function FaqModule(props: {
  state: GeoInputState;
  obsidianQuery: string;
  setObsidianQuery: (v: string) => void;
  obsidianResults: ObsidianSearchResult[];
  obsidianLoading: boolean;
  obsidianError: string | null;
  obsidianSearched: boolean;
  onObsidianSearch: () => void;
  onImportFromObsidian: (r: ObsidianSearchResult) => void;
  onAddFaq: (f: Omit<GeoFaq, "id">) => void;
  onDeleteFaq: (id: string) => void;
  onUpdateFaq: (id: string, patch: Partial<GeoFaq>) => void;
  onToggleFaqDeploy: (id: string, ai: "doubao" | "ernie" | "qwen" | "deepseek") => void;
}) {
  const { state } = props;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // 新建 FAQ 草稿
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const [contact, setContact] = useState("");
  const [landing, setLanding] = useState("");
  const [targetKws, setTargetKws] = useState("");

  const submitNewFaq = () => {
    if (!q.trim() || !a.trim()) return;
    props.onAddFaq({
      question: q.trim(),
      answer: a.trim(),
      contactInfo: contact.trim(),
      landingUrl: landing.trim(),
      targetKeywords: targetKws.split(/[,，]/).map((x) => x.trim()).filter(Boolean),
      deployedToAi: { doubao: false, ernie: false, qwen: false, deepseek: false },
    });
    setQ(""); setA(""); setContact(""); setLanding(""); setTargetKws("");
    setShowAddForm(false);
  };

  return (
    <div className="space-y-3">
      {/* Obsidian 知识库检索 */}
      <div className="p-2 rounded border border-emerald-500/15 bg-emerald-500/[0.02]">
        <div className="flex items-center gap-1.5 mb-1.5">
          <BookOpen size={11} className="text-emerald-400" />
          <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">从 Obsidian 知识库检索 FAQ</span>
        </div>
        <div className="flex items-center gap-1.5 mb-1.5">
          <input
            value={props.obsidianQuery}
            onChange={(e) => props.setObsidianQuery(e.target.value)}
            placeholder="输入检索关键词（如 长沙SEO / 品牌服务）"
            className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-emerald-500/40"
            onKeyDown={(e) => { if (e.key === "Enter") props.onObsidianSearch(); }}
          />
          <button
            onClick={props.onObsidianSearch}
            disabled={props.obsidianLoading || !props.obsidianQuery.trim()}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded disabled:opacity-40"
          >
            {props.obsidianLoading ? <Loader2 size={10} className="animate-spin" /> : <Search size={10} />}
            检索
          </button>
        </div>
        {/* 检索错误 */}
        {props.obsidianError && (
          <div className="flex items-center gap-1.5 px-2 py-1 mb-1 rounded bg-red-500/10 border border-red-500/20">
            <AlertCircle size={10} className="text-red-400 flex-shrink-0" />
            <span className="text-[9px] text-red-300">{props.obsidianError}</span>
          </div>
        )}
        {/* 检索结果 */}
        {props.obsidianSearched && props.obsidianResults.length === 0 && !props.obsidianError && (
          <div className="text-[9px] font-mono text-gray-500 py-2 text-center">
            <Inbox size={14} className="mx-auto mb-1 text-gray-600" />
            Obsidian 中未检索到匹配内容
          </div>
        )}
        {props.obsidianResults.length > 0 && (
          <div className="space-y-1 max-h-[160px] overflow-y-auto">
            {props.obsidianResults.map((r, i) => (
              <div key={i} className="p-1.5 rounded border border-white/[0.04] bg-white/[0.02]">
                <div className="flex items-center justify-between gap-1.5 mb-0.5">
                  <span className="text-[10px] text-gray-200 truncate font-medium">{r.title}</span>
                  <button
                    onClick={() => props.onImportFromObsidian(r)}
                    className="flex items-center gap-0.5 text-[9px] font-mono text-emerald-400 hover:text-emerald-300 flex-shrink-0"
                    title="导入为 FAQ 草稿"
                  >
                    <Plus size={9} /> 导入
                  </button>
                </div>
                {r.summary && <div className="text-[9px] text-gray-500 line-clamp-2">{r.summary}</div>}
                {r.path && (
                  <div className="flex items-center gap-1 text-[8px] font-mono text-emerald-500/70 mt-0.5">
                    <FileText size={8} />
                    <span className="truncate">{r.path}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 添加 FAQ */}
      <div className="p-2 rounded border border-blue-500/15 bg-blue-500/[0.02]">
        <button
          onClick={() => setShowAddForm((v) => !v)}
          className="flex items-center gap-1 text-[9px] font-mono text-blue-300 uppercase tracking-wider w-full"
        >
          {showAddForm ? <ChevronDown size={10} className="rotate-180 transition-transform" /> : <Plus size={10} />}
          手动添加 FAQ 话术
        </button>
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="space-y-1.5 pt-2">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="问题（如 长沙哪家公司做SEO最好？）"
                  className="w-full bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-blue-500/40"
                />
                <textarea
                  value={a}
                  onChange={(e) => setA(e.target.value)}
                  placeholder="标准应答话术（含品牌介绍+核心优势+联系方式植入）"
                  rows={3}
                  className="w-full bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-blue-500/40 resize-none"
                />
                <div className="grid grid-cols-2 gap-1.5">
                  <input
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder="联系方式（电话/微信）"
                    className="bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-blue-500/40"
                  />
                  <input
                    value={landing}
                    onChange={(e) => setLanding(e.target.value)}
                    placeholder="引流落地页 URL"
                    className="bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-blue-500/40"
                  />
                </div>
                <input
                  value={targetKws}
                  onChange={(e) => setTargetKws(e.target.value)}
                  placeholder="目标关键词（逗号分隔）"
                  className="w-full bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-blue-500/40"
                />
                <button
                  onClick={submitNewFaq}
                  disabled={!q.trim() || !a.trim()}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-blue-600/80 hover:bg-blue-600 rounded disabled:opacity-40"
                >
                  <Save size={10} />
                  保存 FAQ
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* FAQ 列表 */}
      {state.faqs.length === 0 ? (
        <EmptyState icon={Brain} text="暂无 FAQ 预埋。从 Obsidian 检索导入，或手动添加标准应答话术。" />
      ) : (
        <div className="space-y-2">
          {state.faqs.map((faq) => {
            const isEditing = editingId === faq.id;
            const deployCount = Object.values(faq.deployedToAi).filter(Boolean).length;
            return (
              <div key={faq.id} className="p-2 rounded border border-white/[0.04] bg-white/[0.01]">
                {/* 问题行 */}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-start gap-1.5 min-w-0">
                    <MessageSquare size={11} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    {isEditing ? (
                      <input
                        value={faq.question}
                        onChange={(e) => props.onUpdateFaq(faq.id, { question: e.target.value })}
                        className="flex-1 bg-white/[0.03] border border-blue-500/30 rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none"
                      />
                    ) : (
                      <span className="text-[11px] text-gray-200 font-medium">{faq.question}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {faq.sourceFromObsidian && (
                      <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-300" title={faq.sourceFromObsidian}>
                        <BookOpen size={9} className="inline" /> Obsidian
                      </span>
                    )}
                    <span className={`text-[8px] font-mono px-1 py-0.5 rounded ${
                      deployCount === 4 ? "bg-emerald-500/15 text-emerald-400" :
                      deployCount > 0 ? "bg-blue-500/15 text-blue-400" :
                      "bg-gray-500/10 text-gray-500"
                    }`}>
                      部署 {deployCount}/4
                    </span>
                    <button onClick={() => setEditingId(isEditing ? null : faq.id)} className="text-gray-500 hover:text-blue-400" title={isEditing ? "收起" : "编辑"}>
                      {isEditing ? <X size={11} /> : <Edit3 size={11} />}
                    </button>
                    <button onClick={() => props.onDeleteFaq(faq.id)} className="text-gray-600 hover:text-red-400" title="删除">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>

                {/* 答案 */}
                {isEditing ? (
                  <textarea
                    value={faq.answer}
                    onChange={(e) => props.onUpdateFaq(faq.id, { answer: e.target.value })}
                    rows={3}
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-300 font-mono outline-none focus:border-blue-500/40 resize-none mb-1"
                  />
                ) : (
                  <div className="text-[10px] text-gray-400 leading-relaxed mb-1 pl-4">{faq.answer}</div>
                )}

                {/* 联系方式 + 引流入口 */}
                <div className="flex items-center gap-2 mb-1 text-[9px] font-mono flex-wrap">
                  {faq.contactInfo && (
                    <span className="flex items-center gap-0.5 text-blue-300">
                      <Phone size={9} /> {faq.contactInfo}
                    </span>
                  )}
                  {faq.landingUrl && (
                    <a href={faq.landingUrl} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 text-cyan-400 hover:text-cyan-300">
                      <Rocket size={9} /> 引流入口 <ExternalLink size={8} />
                    </a>
                  )}
                </div>

                {/* 目标关键词 */}
                {faq.targetKeywords.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap mb-1.5">
                    <span className="text-[8px] font-mono text-gray-600">目标词：</span>
                    {faq.targetKeywords.map((k) => (
                      <span key={k} className="text-[8px] font-mono px-1 py-0.5 rounded bg-blue-500/10 text-blue-300">
                        {k}
                      </span>
                    ))}
                  </div>
                )}

                {/* AI 平台部署状态 */}
                <div className="flex items-center gap-1 pt-1 border-t border-white/[0.04]">
                  <span className="text-[8px] font-mono text-gray-600 mr-0.5">部署到 AI：</span>
                  {AI_PLATFORMS.map((ai) => {
                    const deployed = faq.deployedToAi[ai.key];
                    return (
                      <button
                        key={ai.key}
                        onClick={() => props.onToggleFaqDeploy(faq.id, ai.key)}
                        className={`flex items-center gap-0.5 text-[8px] font-mono px-1.5 py-0.5 rounded border transition-all ${
                          deployed
                            ? "text-white border-transparent"
                            : "bg-white/[0.02] text-gray-600 border-white/[0.06] hover:border-white/[0.12]"
                        }`}
                        style={deployed ? { background: `${ai.color}25`, color: ai.color, borderColor: `${ai.color}50` } : {}}
                        title={`${ai.name} ${deployed ? "已部署" : "未部署"}，点击切换`}
                      >
                        {deployed ? <CheckCircle2 size={8} /> : <XCircle size={8} />}
                        {ai.short}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// 通用子组件：可折叠卡片
// ============================================
function CollapsibleCard(props: {
  title: string;
  icon: typeof Globe;
  color: string;
  count?: number;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const { title, icon: Icon, color, count, collapsed, onToggle, children } = props;
  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full px-2.5 py-2 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <Icon size={12} style={{ color }} />
          <span className="text-[10px] font-semibold" style={{ color }}>{title}</span>
          {count != null && (
            <span className="text-[8px] font-mono px-1 py-0.5 rounded" style={{ background: `${color}20`, color }}>
              {count}
            </span>
          )}
        </div>
        <ChevronDown size={12} className={`text-gray-500 transition-transform ${collapsed ? "" : "rotate-180"}`} />
      </button>
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-2 pt-0">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 通用子组件：空状态
// ============================================
function EmptyState(props: { icon: typeof Inbox; text: string }) {
  const { icon: Icon, text } = props;
  return (
    <div className="flex flex-col items-center justify-center py-6 px-3 text-center">
      <div className="w-10 h-10 rounded-full border border-white/[0.06] bg-white/[0.02] flex items-center justify-center mb-2">
        <Icon size={18} className="text-gray-600" />
      </div>
      <p className="text-[10px] text-gray-500 leading-relaxed max-w-[280px]">{text}</p>
    </div>
  );
}
